import type { ProductCardDto, StoreCardDto } from '@runnerboy/types';
import { Button, EmptyState, Input, Skeleton } from '@runnerboy/ui';
import { Clock3, Search as SearchIcon, Store, TrendingUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { AdBanner, useAds } from '../components/ads';
import { CartConflictSheet, CategoryTile, ProductCard, useCartActions } from '../components/commerce';
import { Page, SectionTitle } from '../components/shell';
import { client, useAsync, useSession } from '../lib/session';

const RECENT_KEY = 'rb.recentSearches';

interface Category {
  slug: string;
  name: string;
  productType: string;
}
const TRENDING_ALCOHOL = ['Kingfisher', 'Whisky', 'Bira', 'Wine', 'Rum'];
const TRENDING_ALWAYS = ['Ice', 'Soda', 'Tonic', 'Chips', 'Water'];

function readRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}
function saveRecent(list: string[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 8)));
  } catch {
    /* ignore */
  }
}

export function SearchPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { location, storeId, ageGate } = useSession();
  const [q, setQ] = useState(sp.get('q') ?? '');
  const [debounced, setDebounced] = useState(q);
  const [recent, setRecent] = useState<string[]>(readRecent);
  const actions = useCartActions();

  const stores = useAsync((s) => client.get<StoreCardDto[]>(`/public/stores?lat=${location?.lat}&lng=${location?.lng}`, s), [location?.lat, location?.lng], { enabled: !!location });
  const store = stores.data?.find((s) => s.id === storeId) ?? stores.data?.[0] ?? null;
  const ads = useAds(['SEARCH_PROMO'], { storeId: store?.id, enabled: !!location });
  const categories = useAsync((s) => client.get<Category[]>('/public/categories', s), []);
  const aisles = (categories.data ?? []).filter((c) => ageGate === 'confirmed' || c.productType !== 'ALCOHOL').slice(0, 8);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  useEffect(() => {
    const n = new URLSearchParams(sp);
    if (debounced) n.set('q', debounced);
    else n.delete('q');
    setSp(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const results = useAsync(
    (s) => client.get<{ items: ProductCardDto[]; normalizedQuery: string | null }>(`/public/stores/${store?.id}/catalog?q=${encodeURIComponent(debounced)}&limit=40`, s),
    [store?.id, debounced],
    { enabled: !!store && debounced.length >= 2 },
  );

  const remember = (term: string) => {
    const next = [term, ...recent.filter((r) => r.toLowerCase() !== term.toLowerCase())];
    setRecent(next);
    saveRecent(next);
  };
  const pick = (term: string) => {
    setQ(term);
    setDebounced(term);
    remember(term);
  };
  const trending = [...(ageGate === 'confirmed' ? TRENDING_ALCOHOL : []), ...TRENDING_ALWAYS];

  return (
    <Page className="pt-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim()) remember(q.trim());
        }}
        className="relative"
        role="search"
      >
        <SearchIcon className="pointer-events-none absolute left-[18px] top-1/2 h-5 w-5 -translate-y-1/2 text-ink-900" aria-hidden />
        <Input
          autoFocus
          enterKeyHint="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={store ? t('catalog.searchIn', { store: store.name }) : t('home.searchPlaceholder', 'Search beer, whisky, soda, ice…')}
          aria-label={t('common.search')}
          className="h-[54px] rounded-full border-0 bg-white pl-12 pr-12 text-[15px] shadow-[var(--shadow-card)] ring-1 ring-ink-900/[0.08]"
        />
        {q && (
          <button type="button" onClick={() => setQ('')} className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full text-ink-400 hover:bg-ink-100" aria-label={t('common.clear', 'Clear')}>
            <X className="h-5 w-5" />
          </button>
        )}
      </form>

      {store && (
        <button onClick={() => nav(`/store/${store.id}`)} className="rb-press mt-2.5 inline-flex h-8 items-center gap-1.5 rounded-full bg-ink-900/[0.05] px-3 text-[12px] font-semibold text-ink-600">
          <Store className="h-3.5 w-3.5" aria-hidden /> {t('search.inStore', 'Searching {{store}}', { store: store.name })}
        </button>
      )}

      {!location || (!stores.loading && !store) ? (
        <EmptyState icon={<Store className="h-8 w-8" />} title={t('home.chooseStore', 'Choose a store first')} body={t('search.needStore', 'Pick a delivery location to see a licensed store near you.')} action={<Button onClick={() => nav('/')}>{t('cart.browse')}</Button>} />
      ) : debounced.length < 2 ? (
        <>
          {ads.SEARCH_PROMO?.[0] && (
            <div className="mt-5">
              <AdBanner ad={ads.SEARCH_PROMO[0]} />
            </div>
          )}
          {recent.length > 0 && (
            <>
              <SectionTitle
                action={
                  <button
                    className="text-xs font-semibold text-ink-500"
                    onClick={() => {
                      setRecent([]);
                      saveRecent([]);
                    }}
                  >
                    {t('common.clear', 'Clear')}
                  </button>
                }
              >
                {t('search.recent', 'Recent searches')}
              </SectionTitle>
              <div className="flex flex-wrap gap-2">
                {recent.map((r) => (
                  <button key={r} onClick={() => pick(r)} className="rb-press inline-flex h-10 items-center gap-1.5 rounded-full bg-white px-4 text-[13.5px] font-medium text-ink-700 ring-1 ring-ink-900/[0.08]">
                    <Clock3 className="h-3.5 w-3.5 text-ink-400" aria-hidden /> {r}
                  </button>
                ))}
              </div>
            </>
          )}
          <SectionTitle eyebrow={t('search.popular', 'Popular')}>{t('search.trending', 'Trending near you')}</SectionTitle>
          <div className="flex flex-wrap gap-2">
            {trending.map((r) => (
              <button key={r} onClick={() => pick(r)} className="rb-press inline-flex h-10 items-center gap-1.5 rounded-full bg-white px-4 text-[13.5px] font-semibold text-ink-900 shadow-[var(--shadow-card)] ring-1 ring-ink-900/[0.06]">
                <TrendingUp className="h-4 w-4 text-marigold-600" aria-hidden /> {r}
              </button>
            ))}
          </div>
          {ageGate !== 'confirmed' && <p className="mt-4 text-xs text-ink-500">{t('catalog.alcoholHidden')}</p>}
          {store && aisles.length > 0 && (
            <>
              <SectionTitle eyebrow={t('home.aisles', 'Aisles')}>{t('search.browse', 'Browse by aisle')}</SectionTitle>
              <div className="grid grid-cols-4 gap-x-3 gap-y-4">
                {aisles.map((c) => (
                  <CategoryTile key={c.slug} slug={c.slug} name={c.name} to={`/store/${store.id}?category=${c.slug}`} />
                ))}
              </div>
            </>
          )}
        </>
      ) : results.loading ? (
        <div className="mt-5 grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[250px] rounded-[22px]" />
          ))}
        </div>
      ) : (results.data?.items.length ?? 0) === 0 ? (
        <EmptyState icon={<SearchIcon className="h-8 w-8" />} title={t('catalog.noResults', { query: debounced })} body={t('catalog.noResultsBody')} />
      ) : (
        <>
          <p className="rb-eyebrow mt-5 text-ink-500">{t('search.results', '{{count}} results', { count: results.data?.items.length ?? 0 })}</p>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {results.data?.items.map((p) => (
              <ProductCard key={p.storeProductId} product={p} actions={actions} />
            ))}
          </div>
        </>
      )}
      <CartConflictSheet actions={actions} />
    </Page>
  );
}
